import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, User } from 'lucide-react';
import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import { AVATAR_IDS, getAvatarPath } from '@/lib/avatarUtils';

interface AvatarSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentAvatar: string; // Si aspetta l'ID (es. "001")
    onSelect: (avatarId: string) => void; // Restituisce l'ID
}

const AvatarSelectionModal: React.FC<AvatarSelectionModalProps> = ({
    isOpen,
    onClose,
    currentAvatar,
    onSelect
}) => {
    // Stato locale per la selezione corrente nel modale
    const [selectedId, setSelectedId] = useState<string>(currentAvatar || '001');

    // Sincronizza lo stato quando il modale si apre
    useEffect(() => {
        if (isOpen) {
            setSelectedId(currentAvatar || '001');
        }
    }, [isOpen, currentAvatar]);

    const handleConfirm = () => {
        onSelect(selectedId);
        // Nota: il genitore si occuperà di chiudere il modale dopo il salvataggio
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            headerContent={
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <User size={24} className="text-clip-500" /> Scegli il tuo Avatar
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Seleziona un'immagine per il tuo profilo.</p>
                </div>
            }
        >
            <div className="space-y-8">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 max-h-[50vh] overflow-y-auto p-2">
                    {AVATAR_IDS.map((id) => {
                        const isSelected = selectedId === id;
                        const src = getAvatarPath(id); // Usa la utility per visualizzare l'immagine

                        return (
                            <motion.button
                                key={id}
                                type="button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setSelectedId(id)}
                                className={`relative rounded-full aspect-square overflow-hidden border-4 transition-all outline-none ${isSelected
                                    ? 'border-clip-500 shadow-xl shadow-clip-200 ring-2 ring-offset-2 ring-clip-200'
                                    : 'border-transparent hover:border-gray-200'
                                    }`}
                            >
                                <img
                                    src={src}
                                    alt={`Avatar ${id}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />

                                <AnimatePresence>
                                    {isSelected && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 bg-clip-500/20 flex items-center justify-center backdrop-blur-[1px]"
                                        >
                                            <div className="bg-white rounded-full p-1.5 shadow-sm">
                                                <Check size={16} className="text-clip-500 stroke-[3px]" />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.button>
                        );
                    })}
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                    <div className="flex gap-3 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-6 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Annulla
                        </button>
                        <PrimaryButton
                            label="Conferma Avatar"
                            onClick={handleConfirm}
                            className="flex-1 sm:flex-none justify-center"
                            disabled={!selectedId}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AvatarSelectionModal;